import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { isNilOrEmpty, nz } from '@shared/helpers/utils.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { getDatiBilancioControl } from '../validators/helpers/dati-bilancio-form.helpers';
import { toNum } from '@shared/helpers/math.helper';
import { cloneDeep } from 'lodash-es';
import { Utilities } from '@core/helpers/utilities';
import { debounceTime, merge, startWith } from 'rxjs';

export const datiBilancioSyncBilancioListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_BilAnno(form, store);
    my_ImportoPatr(form, store);
    my_CapSocSottoscr(form, store);
    my_CapSocVersato(form, store, true);
    my_CapSocVersato(form, store, false);
};

const my_BilAnno = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
    const my_BilAnno1Control = getControl(datiBilancioGroup, 'my_BilAnno1');
    const my_BilAnno2Control = getControl(datiBilancioGroup, 'my_BilAnno2');
    const my_AnnoPrimoEsControl = getDatiBilancioControl(form, 'my_AnnoPrimoEs');
    const my_AnnoSecondoEsControl = getDatiBilancioControl(form, 'my_AnnoSecondoEs');
    const my_AnnoPCapSocControl = getDatiBilancioControl(form, 'my_AnnoPCapSoc');
    const my_AnnoSCapSocControl = getDatiBilancioControl(form, 'my_AnnoSCapSoc');

    my_BilAnno1Control.valueChanges.pipe(withInitialValueDebounced(my_BilAnno1Control)).subscribe((my_BilAnno1: string) => {
        my_AnnoPrimoEsControl.patchValue(my_BilAnno1, { emitEvent: false });
        my_AnnoPCapSocControl.patchValue(my_BilAnno1, { emitEvent: false });

        if (!isNilOrEmpty(my_BilAnno1)) {
            const anno2 = (toNum(my_BilAnno1) + 1).toString(10);
            my_BilAnno2Control.patchValue(anno2, { emitEvent: false });
        }

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            const sez = myVerbaleClone.my_SezioneI__Rilevazione;
            sez.my_Bilancio.my_AnnoPrimoEs = my_BilAnno1;
            sez.my_Bilancio.my_AnnoPCapSoc = my_BilAnno1;
            if (!isNilOrEmpty(my_BilAnno1)) {
                sez.my_DatiBilancio.my_BilAnno2 = (toNum(my_BilAnno1) + 1).toString(10);
            }
            return myVerbaleClone;
        });
    });

    my_BilAnno2Control.valueChanges.pipe(withInitialValueDebounced(my_BilAnno2Control)).subscribe((my_BilAnno2: string) => {
        my_AnnoSecondoEsControl.patchValue(my_BilAnno2, { emitEvent: false });
        my_AnnoSCapSocControl.patchValue(my_BilAnno2, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            const sez = myVerbaleClone.my_SezioneI__Rilevazione;
            sez.my_Bilancio.my_AnnoSecondoEs = my_BilAnno2;
            sez.my_Bilancio.my_AnnoSCapSoc = my_BilAnno2;
            return myVerbaleClone;
        });
    });
};

/** Syncs my_StPaPsAtot1/2 (patrimonio netto totale) into my_Bilancio.my_ImportoPatrPrimoEs/SecondoEs on the store. */
const my_ImportoPatr = (form: FormGroup, store: Store<Revisione53State>): void => {
    const passGroup = getGroup(form, 'my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo');
    const atot1Control = getControl(passGroup, 'my_StPaPsAtot1');
    const atot2Control = getControl(passGroup, 'my_StPaPsAtot2');

    atot1Control.valueChanges.pipe(withInitialValueDebounced(atot1Control)).subscribe((value: string) => {
        getControl(form, 'my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrPrimoEs').patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrPrimoEs = value;
            return myVerbaleClone;
        });
    });

    atot2Control.valueChanges.pipe(withInitialValueDebounced(atot2Control)).subscribe((value: string) => {
        getControl(form, 'my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs').patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs = value;
            return myVerbaleClone;
        });
    });
};

/** Syncs my_StPaPsAI1/2 (capitale sociale sottoscritto) into my_Bilancio.my_CapSocSottoscrPrimoEs/SecondoEs. */
const my_CapSocSottoscr = (form: FormGroup, store: Store<Revisione53State>): void => {
    const passGroup = getGroup(form, 'my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo');
    const ai1Control = getControl(passGroup, 'my_StPaPsAI1');
    const ai2Control = getControl(passGroup, 'my_StPaPsAI2');

    ai1Control.valueChanges.pipe(withInitialValueDebounced(ai1Control)).subscribe((value: string) => {
        getDatiBilancioControl(form, 'my_CapSocSottoscrPrimoEs').patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrPrimoEs = value;
            return myVerbaleClone;
        });
    });

    ai2Control.valueChanges.pipe(withInitialValueDebounced(ai2Control)).subscribe((value: string) => {
        getDatiBilancioControl(form, 'my_CapSocSottoscrSecondoEs').patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs = value;
            return myVerbaleClone;
        });
    });
};

/** Computes my_CapSocVersato = Nz(my_StPaPsAI) - Nz(my_StPaAtAtot) for the given year. */
const my_CapSocVersato = (form: FormGroup, store: Store<Revisione53State>, isFirstYear: boolean): void => {
    const passGroup = getGroup(form, 'my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo');
    const attivoGroup = getGroup(form, 'my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo');
    const aiControl = getControl(passGroup, isFirstYear ? 'my_StPaPsAI1' : 'my_StPaPsAI2');
    const atotControl = getControl(attivoGroup, isFirstYear ? 'my_StPaAtAtot1' : 'my_StPaAtAtot2');
    const targetField = isFirstYear ? 'my_CapSocVersatoPrimoEs' : 'my_CapSocVersatoSecondoEs';

    merge(aiControl.valueChanges.pipe(startWith(aiControl.value)), atotControl.valueChanges.pipe(startWith(atotControl.value)))
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const value = (nz(aiControl.value) - nz(atotControl.value)).toString(10);
            getDatiBilancioControl(form, `${targetField}`).patchValue(value, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                if (isFirstYear) {
                    myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoPrimoEs = value;
                } else {
                    myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs = value;
                }
                return myVerbaleClone;
            });
        });
};
